/**
 * Data Export Component
 * Export historical data in multiple formats (CSV, Excel, PDF)
 */

import { useState } from 'react';
import { 
  Download, FileSpreadsheet, FileText, File, 
  Loader2, Calendar, Droplets, Activity, AlertTriangle,
  CheckCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Data types available for export
const DATA_TYPES = [
  { id: 'levels', name: 'Niveles de Agua', icon: Droplets, color: 'text-blue-400' },
  { id: 'irrigation', name: 'Operaciones de Vaciado', icon: Activity, color: 'text-emerald-400' },
  { id: 'alerts', name: 'Historial de Alertas', icon: AlertTriangle, color: 'text-amber-400' },
];

// Time ranges
const TIME_RANGES = [
  { id: 'day', name: 'Hoy' },
  { id: 'week', name: 'Última Semana' },
  { id: 'month', name: 'Último Mes' },
  { id: 'quarter', name: 'Último Trimestre' },
  { id: 'year', name: 'Último Año' },
  { id: 'all', name: 'Todo el Historial' },
];

// Export formats
const FORMATS = [
  { id: 'csv', name: 'CSV', icon: FileText, description: 'Formato de texto separado por comas' },
  { id: 'excel', name: 'Excel', icon: FileSpreadsheet, description: 'Hoja de cálculo Microsoft Excel' },
  { id: 'json', name: 'JSON', icon: File, description: 'Formato de intercambio de datos' },
];

const DataExport = ({ className = "" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedTypes, setSelectedTypes] = useState(['levels']);
  const [timeRange, setTimeRange] = useState('month');
  const [format, setFormat] = useState('csv');
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(null);

  const toggleDataType = (typeId) => {
    setSelectedTypes(prev => 
      prev.includes(typeId)
        ? prev.filter(t => t !== typeId)
        : [...prev, typeId]
    );
  };

  const handleExport = async () => {
    if (selectedTypes.length === 0) {
      toast.error('Selecciona al menos un tipo de datos');
      return;
    }

    setExporting(true);
    setExportProgress('Preparando exportación...');

    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Request export from backend
      const response = await axios.post(`${API}/export/data`, {
        types: selectedTypes,
        range: timeRange,
        format: format
      }, { 
        headers,
        responseType: 'blob'
      });

      // Create download link
      const blob = new Blob([response.data], { 
        type: format === 'excel' 
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : format === 'json'
            ? 'application/json'
            : 'text/csv'
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const timestamp = new Date().toISOString().split('T')[0];
      const extension = format === 'excel' ? 'xlsx' : format;
      link.download = `centinela_export_${timestamp}.${extension}`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Datos exportados correctamente');
      setIsOpen(false);

    } catch (error) {
      console.error('Export error:', error);
      
      // If backend doesn't have the endpoint yet, generate client-side
      if (error.response?.status === 404) {
        await exportClientSide();
      } else {
        toast.error('Error al exportar datos');
      }
    } finally {
      setExporting(false);
      setExportProgress(null);
    }
  };

  // Client-side export fallback
  const exportClientSide = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const allData = {};

      setExportProgress('Obteniendo datos...');

      // Fetch data for each selected type
      for (const type of selectedTypes) {
        setExportProgress(`Obteniendo ${DATA_TYPES.find(t => t.id === type)?.name}...`);
        
        try {
          if (type === 'levels') {
            const res = await axios.get(`${API}/sensors/history/aggregated?range=${timeRange}`, { headers });
            allData.levels = res.data || [];
          } else if (type === 'irrigation') {
            const res = await axios.get(`${API}/irrigation/history?range=${timeRange}`, { headers });
            allData.irrigation = res.data || [];
          } else if (type === 'alerts') {
            const res = await axios.get(`${API}/alerts?range=${timeRange}`, { headers });
            allData.alerts = res.data || [];
          }
        } catch (e) {
          console.warn(`Could not fetch ${type}:`, e);
          allData[type] = [];
        }
      }

      setExportProgress('Generando archivo...');

      // Generate file based on format
      let content, mimeType, extension;

      if (format === 'json') {
        content = JSON.stringify(allData, null, 2);
        mimeType = 'application/json';
        extension = 'json';
      } else if (format === 'csv') {
        content = generateCSV(allData);
        mimeType = 'text/csv;charset=utf-8';
        extension = 'csv';
      } else {
        // For Excel, generate CSV as fallback
        content = generateCSV(allData);
        mimeType = 'text/csv;charset=utf-8';
        extension = 'csv';
        toast.info('Excel no disponible, exportando como CSV');
      }

      // Download file
      const blob = new Blob(['\ufeff' + content], { type: mimeType }); // BOM for UTF-8
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const timestamp = new Date().toISOString().split('T')[0];
      link.download = `centinela_export_${timestamp}.${extension}`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Datos exportados correctamente');
      setIsOpen(false);

    } catch (error) {
      console.error('Client-side export error:', error);
      toast.error('Error al exportar datos');
    }
  };

  // Generate CSV content
  const generateCSV = (data) => {
    let csv = '';

    // Levels data
    if (data.levels && data.levels.length > 0) {
      csv += '=== NIVELES DE AGUA ===\n';
      csv += 'Fecha/Hora,PARRALILLO (m),SIBERIO (m),CAIDERO (m)\n';
      data.levels.forEach(row => {
        csv += `${row.time || ''},${row.PARRALILLO || ''},${row.SIBERIO || ''},${row.CAIDERO || ''}\n`;
      });
      csv += '\n';
    }

    // Irrigation data
    if (data.irrigation && data.irrigation.length > 0) {
      csv += '=== OPERACIONES DE VACIADO ===\n';
      csv += 'ID,Punto de Control,Presa,Fecha Apertura,Fecha Cierre,Horas,Azadas,Operador,Notas\n';
      data.irrigation.forEach(row => {
        csv += `${row.id || ''},${row.control_point_name || ''},${row.dam || ''},${row.opened_at || ''},${row.closed_at || ''},${row.hours_water || row.duration_hours || ''},${row.azadas || ''},${row.operator || ''},${(row.notes || '').replace(/,/g, ';')}\n`;
      });
      csv += '\n';
    }

    // Alerts data
    if (data.alerts && data.alerts.length > 0) {
      csv += '=== HISTORIAL DE ALERTAS ===\n';
      csv += 'ID,Sensor,Tipo,Nivel,Mensaje,Fecha,Reconocida\n';
      data.alerts.forEach(row => {
        csv += `${row.id || ''},${row.sensor_name || ''},${row.event_type || ''},${row.level || ''},${(row.message || '').replace(/,/g, ';')},${row.created_at || row.timestamp || ''},${row.acknowledged ? 'Sí' : 'No'}\n`;
      });
    }

    return csv;
  };

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setIsOpen(true)}
        className={`text-zinc-400 hover:text-cyan-400 h-8 w-8 md:h-10 md:w-10 p-0 ${className}`}
        data-testid="export-btn"
      >
        <Download className="w-5 h-5 md:w-6 md:h-6" />
      </Button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <Download className="w-5 h-5 text-cyan-400" />
              Exportar Datos
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Descarga el historial de datos en el formato que prefieras
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Data Types Selection */}
            <div className="space-y-3">
              <Label className="text-sm font-medium text-zinc-300">Tipo de Datos</Label>
              <div className="grid gap-2">
                {DATA_TYPES.map((type) => (
                  <div
                    key={type.id}
                    onClick={() => toggleDataType(type.id)}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                      selectedTypes.includes(type.id)
                        ? 'bg-zinc-800 border-cyan-500/50'
                        : 'bg-zinc-800/50 border-zinc-700 hover:border-zinc-600'
                    }`}
                  >
                    <Checkbox 
                      checked={selectedTypes.includes(type.id)}
                      className="border-zinc-600"
                    />
                    <type.icon className={`w-4 h-4 ${type.color}`} />
                    <span className="text-sm text-zinc-200">{type.name}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Time Range Selection */}
            <div className="space-y-3">
              <Label className="text-sm font-medium text-zinc-300">Período</Label>
              <Select value={timeRange} onValueChange={setTimeRange}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-100">
                  <Calendar className="w-4 h-4 mr-2 text-zinc-400" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  {TIME_RANGES.map((range) => (
                    <SelectItem key={range.id} value={range.id}>
                      {range.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Format Selection */}
            <div className="space-y-3">
              <Label className="text-sm font-medium text-zinc-300">Formato</Label>
              <div className="grid grid-cols-3 gap-2">
                {FORMATS.map((fmt) => (
                  <div
                    key={fmt.id}
                    onClick={() => setFormat(fmt.id)}
                    className={`flex flex-col items-center gap-2 p-3 rounded-lg border cursor-pointer transition-all ${
                      format === fmt.id
                        ? 'bg-cyan-500/20 border-cyan-500/50'
                        : 'bg-zinc-800/50 border-zinc-700 hover:border-zinc-600'
                    }`}
                  >
                    <fmt.icon className={`w-6 h-6 ${format === fmt.id ? 'text-cyan-400' : 'text-zinc-400'}`} />
                    <span className={`text-xs font-medium ${format === fmt.id ? 'text-cyan-400' : 'text-zinc-400'}`}>
                      {fmt.name}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Export Progress */}
            {exporting && exportProgress && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-cyan-500/10 border border-cyan-500/30">
                <Loader2 className="w-4 h-4 animate-spin text-cyan-400" />
                <span className="text-sm text-cyan-400">{exportProgress}</span>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              onClick={() => setIsOpen(false)}
              disabled={exporting}
              className="text-zinc-400"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleExport}
              disabled={exporting || selectedTypes.length === 0}
              className="bg-cyan-600 hover:bg-cyan-700 text-white"
            >
              {exporting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Exportando...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Exportar
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DataExport;
